import { BasicMenuItemOption } from 'ui/menu';
import {
  Bool,
  booleanResponseMap,
  CONVERSION_EVENT_ID_DELIMITER,
  initialTableFilterInfoInput,
} from '../constants';
import {
  ExperimentFilterInfoInput,
  ExperimentsTableMenuItem,
  ExperimentStatusDefinition,
  SystemConversionEventDefinition,
  TableFilter,
} from '../types';

type GenerateSubMenuOptionArg = {
  parentMenuId: TableFilter;
  menuItems: ExperimentsTableMenuItem[];
  translationObject?: Record<string, string>;
};

const validWinningCriteria = [
  TableFilter.EXPERIMENT_SYSTEM_WINNING_CRITERIA,
  TableFilter.EXPERIMENT_CUSTOM_WINNING_CRITERIA,
];

/**
 * Generates a menu id for a given filter.
 * By using the parent filter id and the current filter id
 */
export const encodeMenuId = (tableFilter: TableFilter, value: string) => `${tableFilter}-${value}`;

/**
 * Decodes the menu id into it's components of the parent filter id
 * and the corresponding filter id
 */
export const decodeMenuId = (menuId: string) => {
  const [tableFilter, value] = menuId.split(/-(.*)/s);
  return {
    tableFilter: <TableFilter>tableFilter,
    value,
  };
};

/**
 * The winning criteria can be based on either a system or custom event
 * This method tags a given winning criteria table filter based on it's type
 *
 * This helps us to update the right url search param based on the type of
 * winning criteria filter which is selected.
 */
export const processTableFilterForSearchParam = (
  tableFilter: TableFilter,
  selectedValue: string,
) => {
  if (tableFilter === TableFilter.EXPERIMENT_WINNING_CRITERIA) {
    return Number.isNaN(parseInt(selectedValue, 10))
      ? TableFilter.EXPERIMENT_SYSTEM_WINNING_CRITERIA
      : TableFilter.EXPERIMENT_CUSTOM_WINNING_CRITERIA;
  }
  return tableFilter;
};

/**
 * The winning criteria can be based on either a system or custom event
 * This method combines the two event types under a single winning criteria table filter umbrella.
 */
export const processTableFilterForSelectedFilters = (tableFilter: TableFilter) => {
  if (validWinningCriteria.includes(tableFilter)) {
    return TableFilter.EXPERIMENT_WINNING_CRITERIA;
  }
  return tableFilter;
};

/**
 * Generating the standard basic menu format
 * from a given list of options.
 */
export const generateSubMenuOptions = ({
  parentMenuId,
  menuItems,
  translationObject,
}: GenerateSubMenuOptionArg): BasicMenuItemOption[] =>
  menuItems.map(({ id, labelKey, isTitle = false }) => ({
    id: encodeMenuId(parentMenuId, id),
    label: translationObject?.[labelKey] || labelKey,
    isTitle,
  }));

/**
 * A helper function which inserts a new element when added to a list
 * or removes an element from a list if it's already present.
 */
export function updateMultiOptionSelection<T>(selectedOptions: T[], option: T) {
  const indexOfOption = selectedOptions.indexOf(option);
  let updatedSelectedOptions;
  if (indexOfOption === -1) {
    updatedSelectedOptions = [...selectedOptions, option];
  } else {
    updatedSelectedOptions = [...selectedOptions];
    updatedSelectedOptions.splice(indexOfOption, 1);
  }
  return updatedSelectedOptions;
}

export const selectedFiltersToTableFilterInfo = (
  selectedFilters: string[],
  selectedTab: string[],
): ExperimentFilterInfoInput =>
  selectedFilters.reduce(
    (acc, currFilterId) => {
      const { tableFilter, value } = decodeMenuId(currFilterId);

      switch (tableFilter) {
        case TableFilter.EXPERIMENT_MODIFIED_BY:
        case TableFilter.EXPERIMENT_TEST_SUBJECTS:
        case TableFilter.CAMPAIGN_MEDIUM:
        case TableFilter.CAMPAIGN_TYPE:
          return {
            ...acc,
            [tableFilter]: [...(acc[tableFilter] ?? []), value],
          };

        case TableFilter.CAMPAIGN_LABELS:
          return {
            ...acc,
            [tableFilter]: [...(acc[tableFilter] ?? []), parseInt(value, 10)],
          };

        case TableFilter.EXPERIMENT_WINNING_CRITERIA: {
          const isSystemEvent = Number.isNaN(parseInt(value, 10));

          return {
            ...acc,
            [tableFilter]: {
              ...acc[tableFilter],
              ...(isSystemEvent && {
                systemConversionEvents: [
                  ...(acc[tableFilter]?.systemConversionEvents ?? []),
                  value as SystemConversionEventDefinition,
                ],
              }),
              ...(!isSystemEvent && {
                conversionEventIds: [
                  ...(acc[tableFilter]?.conversionEventIds ?? []),
                  ...value.split(CONVERSION_EVENT_ID_DELIMITER).map((id) => parseInt(id, 10)),
                ],
              }),
            },
          };
        }

        case TableFilter.EXPERIMENT_WINNER_SELECTED:
        case TableFilter.EXPERIMENT_SPLIT_VARIATIONS_RANDOMLY:
          return {
            ...acc,
            [tableFilter]: booleanResponseMap[value as Bool],
          };

        default:
          return acc;
      }
    },
    { ...initialTableFilterInfoInput, statuses: selectedTab as ExperimentStatusDefinition[] },
  );
